import { useContext, useId } from 'react';
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json';

/**
 * MiniChart — generic, dependency-free SVG/flex chart element for reactive-json.
 *
 * Config-first rationale: YAML can declare data + labels, but it cannot compute
 * bar heights (value/max), donut arc lengths (trigonometric strokeDasharray) or
 * a sparkline path. This is therefore generic charting INFRA (the "capacité
 * réutilisable" that justifies code under « générique-ou-rien »); every concrete
 * usage stays content in YAML. No chart.js — matches the DS custom-SVG look.
 *
 * Usage (YAML):
 *   - type: MiniChart
 *     variant: bars            # bars | donut | sparkline
 *     data: ~~.dashboard.hours.byDay
 *     unit: h
 *     max: 10
 *
 * Data shapes (all keys lenient — DS parity aliases accepted):
 *   bars      : [{ label|d, value|h }]
 *   donut     : [{ label, value|val, color? }]   + centerValue / centerLabel
 *   sparkline : [number] or [{ value }]
 */

const PALETTE = [
  'var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)',
  'var(--chart-5)', 'var(--chart-6)', 'var(--chart-7)', 'var(--chart-8)',
];

const fmt = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
};

function Bars({ data, unit = '', max }) {
  const items = data.map((it) => ({
    label: it?.label ?? it?.d ?? '',
    value: Number(it?.value ?? it?.h ?? 0) || 0,
  }));
  const peak = Number(max) > 0 ? Number(max) : Math.max(1, ...items.map((i) => i.value));
  // Alternative textuelle a11y : role="img" rend le groupe comme une image unique
  // nommée par la série (les libellés/valeurs internes ne sont plus lus en vrac).
  const a11yLabel = items.map((it) => `${it.label} : ${it.value > 0 ? fmt(it.value) + (unit ? ' ' + unit : '') : '0'}`).join(', ');
  return (
    <div className="flex items-end gap-3 h-[150px] pt-2" role="img" aria-label={a11yLabel}>
      {items.map((it, i) => {
        const pct = Math.max(0, Math.min(100, (it.value / peak) * 100));
        const on = it.value > 0;
        return (
          <div key={i} className="flex-1 flex flex-col items-center justify-end gap-2 h-full">
            <span className={`text-[0.6875rem] font-semibold tabular-nums ${on ? 'text-slate-700' : 'text-slate-400'}`}>
              {on ? `${fmt(it.value)}${unit ? ' ' + unit : ''}` : '—'}
            </span>
            <div
              className="w-full max-w-[38px] rounded-t-md rounded-b-[3px]"
              style={{
                height: `${pct}%`,
                minHeight: on ? '4px' : '0',
                background: on ? 'var(--gradient-primary)' : 'var(--slate-200)',
                boxShadow: on ? '0 4px 10px rgb(90 79 224 / 0.25)' : 'none',
              }}
              aria-hidden="true"
            />
            <span className="text-[0.6875rem] text-slate-500">{it.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function Donut({ data, centerValue, centerLabel }) {
  const items = data
    .map((it, i) => ({
      label: it?.label ?? '',
      value: Number(it?.value ?? it?.val ?? 0) || 0,
      color: it?.color || PALETTE[i % PALETTE.length],
    }))
    .filter((d) => d.value > 0);
  const size = 120, stroke = 20, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const total = items.reduce((s, d) => s + d.value, 0) || 1;
  let offset = 0;

  return (
    <div className="flex items-center gap-[18px]">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-none" aria-hidden="true">
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--slate-150)" strokeWidth={stroke} />
          {items.map((d, i) => {
            const len = (d.value / total) * c;
            const el = (
              <circle
                key={i}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={d.color}
                strokeWidth={stroke}
                strokeDasharray={`${len} ${c - len}`}
                strokeDashoffset={-offset}
              />
            );
            offset += len;
            return el;
          })}
        </g>
        {centerValue != null && (
          <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle"
            style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, fill: 'var(--text-strong)' }}>
            {centerValue}
          </text>
        )}
        {centerLabel && (
          <text x="50%" y="61%" textAnchor="middle" dominantBaseline="middle"
            style={{ fontFamily: 'var(--font-sans)', fontSize: 9, fontWeight: 600, fill: 'var(--text-muted)', letterSpacing: '0.1em' }}>
            {centerLabel}
          </text>
        )}
      </svg>
      <div className="flex flex-col gap-[9px] flex-1 min-w-0">
        {items.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-[9px] h-[9px] rounded-[3px] flex-none" style={{ background: d.color }} aria-hidden="true" />
            <span className="flex-1 text-slate-700 truncate">{d.label}</span>
            <span className="font-bold text-slate-900 tabular-nums">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Sparkline({ data, max, color = 'var(--iris-600)', fill = 'var(--iris-500)' }) {
  const gid = 'spark' + useId().replace(/:/g, '');
  const vals = data.map((it) => Number(typeof it === 'object' ? it?.value ?? it?.h ?? 0 : it) || 0);
  if (vals.length < 2) return null;
  const w = 100, h = 32, peak = Number(max) > 0 ? Number(max) : Math.max(1, ...vals);
  const step = w / (vals.length - 1);
  const pts = vals.map((v, i) => [i * step, h - (v / peak) * (h - 4) - 2]);
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${w} ${h} L0 ${h} Z`;
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block" aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fill} stopOpacity="0.28" />
          <stop offset="100%" stopColor={fill} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function MiniChart({ props = {} }) {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const ev = (v) => {
    try {
      return evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext });
    } catch {
      return v;
    }
  };

  const variant = ev(props.variant) || 'bars';
  const data = ev(props.data);
  const rows = Array.isArray(data) ? data : [];
  if (!rows.length) {
    return <div className="flex items-center justify-center h-[120px] text-sm text-slate-500">Aucune donnée</div>;
  }

  if (variant === 'donut') {
    return <Donut data={rows} centerValue={ev(props.centerValue)} centerLabel={ev(props.centerLabel)} />;
  }
  if (variant === 'sparkline') {
    return <Sparkline data={rows} max={ev(props.max)} color={ev(props.color)} fill={ev(props.fill)} />;
  }
  return <Bars data={rows} unit={ev(props.unit) || ''} max={ev(props.max)} />;
}

export default MiniChart;
